import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid-pro';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { ThemeProvider } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { displayErrorMessage } from 'utils/tools_functions';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CancelIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';
import { useTranslation } from 'react-i18next';
import { StyledTooltip } from './CustomGenericDataGridUtils';
import { removeGroupementParcel, removeParcel } from 'services/API/Exploitation';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CheckIcon from '@mui/icons-material/Check';

const useDataGridParcelsActions = (
  rows,
  refreshData,
  errorsFormDefault,
  setEditData,
  setModalGroup,
  openOrganicStateDialog,
) => {
  const [rowModesModel, setRowModesModel] = useState({});
  const [actionId, setActionId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [modalProps, setModalProps] = useState({
    resolve: (value) => {},
    reject: () => {},
  });
  const [openDeleteGroupingDialog, setOpenDeleteGroupingDialog] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const open = Boolean(isMenuOpen);

  const { t } = useTranslation();

  const handleSaveClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View },
    });
  };

  const handleEditClick = (id) => () => {
    setRowModesModel((prevRowModesModel) => {
      if (Object.keys(prevRowModesModel).length === 0) {
        return { [id]: { mode: GridRowModes.Edit } };
      } else {
        let resetPrevRowModesModel = {};
        for (const key in prevRowModesModel) {
          if (prevRowModesModel.hasOwnProperty(key)) {
            resetPrevRowModesModel[key] = { mode: GridRowModes.View };
          }
        }
        return { ...resetPrevRowModesModel, [id]: { mode: GridRowModes.Edit, fromAction: true } };
      }
    });
  };

  const handleCancelClick = (id) => (field) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
    errorsFormDefault.formError = false;

    for (const key in errorsFormDefault) {
      if (typeof errorsFormDefault[key] === 'object') {
        errorsFormDefault[key]['message'] = '';
      }
    }
  };

  const handleDeleteDialogOpen = (id) => {
    setActionId(id);
    setOpenDeleteDialog(true);
  };

  const handleGroupingMenu = (event, row) => {
    setMenuRow(row);
    setIsMenuOpen(event.currentTarget);
  };

  const handleCloseGroupingMenu = () => setIsMenuOpen(null);

  const handleDeletegroupingDialog = (id) => {
    setActionId(id);
    setIsMenuOpen(null);
    setOpenDeleteGroupingDialog(true);
  };

  const handleUpdategroupingDialog = (row) => {
    setIsMenuOpen(null);
    const [parentId] = row.parentId;
    const children = rows
      .filter((row) => row.parentId.length > 1 && row.parentId.includes(parentId))
      .flatMap((row) => row.id);
    const editData = {
      parent: parentId,
      children,
    };

    setEditData(editData);
    setModalGroup(true);
  };

  const getActionsParcels = (id, row) => {
    const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
    const isParent = row.isParent;
    const isChild = row.parentId.length > 1;
    if (isInEditMode && !isChild) {
      return [
        <div>
          <StyledTooltip placement="top" title={t('common.validate')}>
            <GridActionsCellItem
              icon={<CheckIcon sx={{ paddingLeft: '2px !important', color: '#277134' }} />}
              label={t('common.validate')}
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />
          </StyledTooltip>
          <StyledTooltip placement="top" title={t('common.cancel')}>
            <GridActionsCellItem
              icon={<CancelIcon sx={{ paddingLeft: '2px !important' }} />}
              label={t('common.cancel')}
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          </StyledTooltip>
        </div>,
      ];
    }
    if (!isChild) {
      return [
        <div>
          <StyledTooltip placement="top" title={t('action.edit')}>
            <GridActionsCellItem
              icon={<EditIcon sx={{ paddingLeft: '2px !important' }} />}
              label={t('action.edit')}
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
            />
          </StyledTooltip>

          <StyledTooltip placement="top" title={t('common.delete')}>
            <GridActionsCellItem
              icon={<DeleteIcon sx={{ paddingLeft: '2px !important' }} />}
              label={t('common.delete')}
              onClick={() => handleDeleteDialogOpen(id)}
              color="inherit"
            />
          </StyledTooltip>
          {isParent && (
            <React.Fragment>
              <StyledTooltip placement="top" title={t('action.manageGrouping')}>
                <GridActionsCellItem
                  icon={<MoreVertIcon sx={{ paddingLeft: '2px !important' }} />}
                  label={t('action.manageGrouping')}
                  onClick={(e) => handleGroupingMenu(e, row)}
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-expanded={open ? 'true' : undefined}
                  aria-haspopup="true"
                  color="inherit"
                />
              </StyledTooltip>
              <Menu
                id="basic-menu"
                anchorEl={isMenuOpen}
                open={open}
                onClose={handleCloseGroupingMenu}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                <MenuItem
                  className="menu-item menu-item__border"
                  onClick={() => {
                    handleUpdategroupingDialog(menuRow);
                  }}
                >
                  {t('dialog.editGrouping')}
                </MenuItem>
                <MenuItem
                  className="menu-item"
                  onClick={() => handleDeletegroupingDialog(menuRow?.id)}
                >
                  {t('dialog.deleteGrouping')}
                </MenuItem>
              </Menu>
            </React.Fragment>
          )}
        </div>,
      ];
    } else {
      return [<div></div>];
    }
  };

  const handleDeleteClick = async () => {
    setOpenDeleteDialog(false);
    const parcelToDelete = rows.find((row) => row.id === actionId);
    try {
      await removeParcel(parcelToDelete.exploitationId, parcelToDelete.id);
      toast.success(t('success.parcelDeleted', { name: parcelToDelete.name }));
      refreshData();
    } catch (error) {
      toast.error(displayErrorMessage('ERR_DELETE_PARCEL'));
    }
  };

  const handleDeleteGroupingClick = async () => {
    setOpenDeleteGroupingDialog(false);
    const parentParcel = rows.find((row) => row.id === actionId);
    try {
      await removeGroupementParcel(parentParcel.exploitationId, parentParcel.id);
      toast.success(t('success.groupingDeleted'));
      refreshData();
    } catch (error) {
      toast.error(displayErrorMessage('ERR_DELETE_PARCEL_GROUP'));
    }
  };

  const renderDeleteDialog = () => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="xs" open={openDeleteDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>{t('dialog.confirmDeleteParcel')}</DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClick}>
              {t('common.yes')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenDeleteDialog(false)}>
              {t('common.no')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  const renderOrganicStateModal = () => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="xs" open={openOrganicStateDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>{t('dialog.conversionDateWarning')}</DialogContent>
          <DialogActions>
            <Button onClick={() => modalProps?.resolve()}>
              {t('common.validate')} <DoneIcon />
            </Button>
            <Button onClick={() => modalProps?.reject()}>
              {t('common.cancel')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };
  const renderDeletegroupingDialog = () => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="sm" open={openDeleteGroupingDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>{t('dialog.confirmDeleteGrouping')}</DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteGroupingClick}>
              {t('common.yes')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenDeleteGroupingDialog(false)}>
              {t('common.no')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  return {
    rowModesModel,
    setRowModesModel,
    getActionsParcels,
    renderDeleteDialog,
    renderDeletegroupingDialog,
    renderOrganicStateModal,
    setModalProps,
  };
};

export default useDataGridParcelsActions;
